import React, { createContext, useContext, ReactNode, useMemo, useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

const LIBRARIES: ("places")[] = ['places'];
const USE_DEV_MAPS = process.env.REACT_APP_USE_DEV_MAPS === 'true';
const IS_PROD = process.env.NODE_ENV === 'production';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  isDevMode: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
  isDevMode: false,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

const GoogleMapsInner: React.FC<{ apiKey: string; children: ReactNode }> = ({ apiKey, children }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const [fallbackToDev, setFallbackToDev] = useState(false);

  useEffect(() => {
    if (loadError) {
      console.warn('Google Maps failed to load, falling back to OpenStreetMap:', loadError.message);
      setFallbackToDev(true);
    }
  }, [loadError]);

  useEffect(() => {
    // Google Maps calls window.gm_authFailure when billing/auth fails at runtime
    (window as any).gm_authFailure = () => {
      console.warn('Google Maps auth failure (billing not enabled) — falling back to OpenStreetMap');
      setFallbackToDev(true);
    };
    return () => { delete (window as any).gm_authFailure; };
  }, []);

  const value = useMemo(
    () => ({
      isLoaded: fallbackToDev ? false : isLoaded,
      loadError,
      // "Dev mode" here means "use OpenStreetMap fallback autocomplete".
      // UI labels are controlled in the autocomplete component.
      isDevMode: fallbackToDev,
    }),
    [isLoaded, loadError, fallbackToDev]
  );

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const GoogleMapsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const missingKeyError = new Error('Google Maps API key is not configured');

  // Allow explicit dev maps only outside production.
  if (!IS_PROD && (USE_DEV_MAPS || !apiKey)) {
    return (
      <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: undefined, isDevMode: true }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  // In production without a Google key, use backend-proxied address search.
  if (!apiKey) {
    return (
      <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: undefined, isDevMode: true }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  return (
    <GoogleMapsInner apiKey={apiKey}>
      {children}
    </GoogleMapsInner>
  );
};
